import fs from 'node:fs';
import path from 'node:path';
import { isMainThread } from 'node:worker_threads';
import {
  assertBackgroundIndexingAllowed,
  yieldForBridgeInteractivePriority,
} from '../../../files/browser/in-flight.js';
import { isSymbolIndexDeferredError } from '../deferred-indexing.js';
import { withCodeNavCacheSqlite } from '../../../sqlite/code-nav-cache/database.js';
import { enrichTagsWithModulePathsAsync } from '../../enrich-tag-module-paths-async.js';
import { isPathGitIgnored } from '../../gitignore-filter.js';
import { isCodeNavSupportedPath } from '../../language-registry.js';
import { extractTagsFromSource } from '../../parser-runtime.js';
import { createCodeNavYieldState } from '../../yield-during-work.js';
import type { SymbolImportBinding, SymbolTag } from '../../types.js';
import { extractImportBindingsFromSource } from '../imports/index.js';
import { MAX_INDEX_FILE_BYTES } from './read-index-source.js';
import { listWorkspaceFiles } from './workspace-files.js';

export type FileSymbolData = {
  source: string | null;
  tags: SymbolTag[];
  imports: SymbolImportBinding[];
};

export async function extractFileSymbolData(
  resolved: string,
  relPath: string,
  workspaceFilesOverride?: ReadonlySet<string>,
): Promise<FileSymbolData> {
  await yieldForBridgeInteractivePriority();
  const absPath = path.join(resolved, relPath);

  let tags: SymbolTag[] = [];
  let source: string | null = null;
  if (isCodeNavSupportedPath(relPath) && !(await isPathGitIgnored(resolved, relPath))) {
    try {
      assertBackgroundIndexingAllowed();
      let fileSource: string | null = null;
      if (isMainThread) {
        const stat = await fs.promises.stat(absPath);
        if (stat.isFile() && stat.size <= MAX_INDEX_FILE_BYTES) {
          assertBackgroundIndexingAllowed();
          fileSource = await fs.promises.readFile(absPath, 'utf8');
        }
      } else {
        const stat = fs.statSync(absPath);
        if (stat.isFile() && stat.size <= MAX_INDEX_FILE_BYTES) {
          assertBackgroundIndexingAllowed();
          fileSource = fs.readFileSync(absPath, 'utf8');
        }
      }
      source = fileSource;
      if (source != null) {
        await yieldForBridgeInteractivePriority();
        tags = await extractTagsFromSource(relPath, source);
      }
    } catch (err) {
      if (isSymbolIndexDeferredError(err)) throw err;
      tags = [];
      source = null;
    }
  }

  await yieldForBridgeInteractivePriority();
  const workspaceFiles =
    workspaceFilesOverride ??
    (await withCodeNavCacheSqlite((db) => listWorkspaceFiles(db, resolved, relPath)));
  const imports =
    source != null ? await extractImportBindingsFromSource(relPath, source, workspaceFiles) : [];

  const yieldState = createCodeNavYieldState();
  return {
    source,
    tags: await enrichTagsWithModulePathsAsync(tags, relPath, workspaceFiles, yieldState),
    imports,
  };
}
